import React from 'react';
import PropTypes from 'prop-types';
import { useCloseOnEscape } from '../../../hooks/useCloseOnEscape';
import {
  CheckCircleIcon,
  EventIcon,
  PeopleIcon
} from '../../icons';
import CalendarIcon from '../../icons/calendar-icon.svg';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1508921340878-ba53e1f016ec?w=80&h=80&fit=crop';

const SuccessModal = ({
  bookingData,
  onClose,
  onExploreMore,
  pricingDetails,
  venue,
  paymentSetup
}) => {
  useCloseOnEscape(!!venue, onClose);
  if (!venue) return null;

  // Get primary image from API response
  const getPrimaryImage = () => {
    // First try primaryImageUrl if available
    if (venue.primaryImageUrl) {
      return venue.primaryImageUrl;
    }
    // Then try to find primary image in images array
    if (venue.images && Array.isArray(venue.images)) {
      const primaryImage = venue.images.find(img => 
        img.is_primary === true || img.image_type === 'primary'
      );
      if (primaryImage && primaryImage.image_url) {
        return primaryImage.image_url;
      }
      // Fallback to first image if no primary found
      if (venue.images.length > 0 && venue.images[0].image_url) {
        return venue.images[0].image_url;
      }
    }
    // Fallback to legacy image property
    if (venue.image) {
      return venue.image;
    }
    // Final fallback placeholder
    return FALLBACK_IMAGE;
  };

  const venueImage = getPrimaryImage();
  const depositFirst =
    paymentSetup?.depositRequired && paymentSetup?.showPrompt && !paymentSetup?.depositCompleted;

  return (
    <div className="success-modal-overlay" onClick={onClose} role="presentation">
      <div className="success-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <div className="success-modal-content">
          {depositFirst ? (
            <div className="success-payment-setup success-payment-setup--primary">
              <p className="success-payment-setup__title">Pay your deposit to send your request</p>
              <p className="success-payment-setup__body">
                This venue requires a deposit before we share your inquiry with them. Pay securely with Stripe now;
                the remaining balance (including card processing) is due after the venue approves.
              </p>
              {paymentSetup?.payUrl ? (
                <a href={paymentSetup.payUrl} className="success-payment-setup__button">
                  Pay deposit with Stripe
                </a>
              ) : null}
            </div>
          ) : (
            <div className="success-header">
              <div className="success-icon-wrapper">
                <div className="success-icon" aria-hidden="true">
                  <CheckCircleIcon />
                </div>
              </div>
              <h2 className="success-title">Booking inquiry sent successfully</h2>
              <p className="success-message">
                We&apos;ve shared your request with the venue. You&apos;ll hear back once they review it.
              </p>
            </div>
          )}

          <div className="booking-details-card">
            <div className="booking-image flex justify-center items-center w-full">
              <img
                src={venueImage}
                alt={venue.name}
                onError={(event) => {
                  event.target.src = FALLBACK_IMAGE;
                }}
              />
            </div>
            <div className="booking-info w-full">
              <h3 className="booking-venue-name">{venue.name}</h3>
              <div className="booking-details">
                <div className="booking-detail-item">
                  <img src={CalendarIcon} alt="Calendar icon" className="detail-icon" />
                  <div className='text-left inline-block'>
                    <span className='block md:inline'>{bookingData.startTime} – {bookingData.endTime},</span>
                    <span className='block md:inline'>{" "}{bookingData.date}</span>
                    </div>
                </div>
                <div className="booking-detail-item">
                  <EventIcon className="detail-icon" aria-hidden="true" />
                  <span>{bookingData.activity}</span>
                </div>
                <div className="booking-detail-item">
                  <PeopleIcon className="detail-icon" aria-hidden="true" />
                  <span>{bookingData.attendees} guests</span>
                </div>
              </div>
            </div>
          </div>

          {!depositFirst ? (
            <div className="success-cta">
              <p className="cta-text">
                In the meantime,{' '}
                <button type="button" className="explore-link" onClick={onExploreMore}>
                  keep exploring more venues
                </button>{' '}
                to find other great options catered to your needs
              </p>
            </div>
          ) : null}
          {paymentSetup?.showPrompt && !paymentSetup?.depositRequired ? (
            <div className="success-payment-setup">
              <p className="success-payment-setup__title">Next step: add your payment method</p>
              <p className="success-payment-setup__body">
                After the venue confirms your booking, pay securely with Stripe. Your card is charged as soon as
                you complete payment — Occasia does not store card numbers.
              </p>
              {paymentSetup?.actionUrl ? (
                <button
                  type="button"
                  className="success-payment-setup__button"
                  onClick={() => window.open(paymentSetup.actionUrl, '_blank', 'noopener,noreferrer')}
                >
                  Add payment method securely
                </button>
              ) : (
                <p className="success-payment-setup__pending">
                  Payment setup link will be available once the payments API is enabled.
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

SuccessModal.propTypes = {
  bookingData: PropTypes.shape({
    activity: PropTypes.string.isRequired,
    attendees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onExploreMore: PropTypes.func.isRequired,
  pricingDetails: PropTypes.shape({
    hourlyRate: PropTypes.number,
    duration: PropTypes.number,
    cleaningFee: PropTypes.number,
    processingFee: PropTypes.number,
    estimatedTotal: PropTypes.number,
    isDiscounted: PropTypes.bool
  }),
  paymentSetup: PropTypes.shape({
    showPrompt: PropTypes.bool,
    actionUrl: PropTypes.string
  }),
  venue: PropTypes.shape({
    address: PropTypes.string,
    area: PropTypes.string,
    image: PropTypes.string,
    primaryImageUrl: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({
      image_url: PropTypes.string,
      image_type: PropTypes.string,
      is_primary: PropTypes.bool
    })),
    name: PropTypes.string.isRequired
  })
};

SuccessModal.defaultProps = {
  pricingDetails: null,
  paymentSetup: null,
  venue: null
};

export default SuccessModal;
